import type {
  DemoComplianceData,
  DemoDeliveryData,
  DemoDispatchData,
  DemoPodData,
  DemoTimelineEvent,
} from './demoData';

const DOCKET_STATE_PREFIX = 'gati_demo_docket_';
const POD_IMAGE_PREFIX = 'gati_pod_image_';

export interface DocketDemoState {
  opsStatus?: string;
  compliance?: Partial<DemoComplianceData>;
  dispatch?: Partial<DemoDispatchData>;
  delivery?: Partial<DemoDeliveryData>;
  pod?: Partial<DemoPodData>;
  extraTimeline?: DemoTimelineEvent[];
}

export function loadDocketDemoState(docketId: string): DocketDemoState {
  try {
    const raw = localStorage.getItem(`${DOCKET_STATE_PREFIX}${docketId}`);
    if (!raw) return {};
    return JSON.parse(raw) as DocketDemoState;
  } catch {
    return {};
  }
}

export function saveDocketDemoState(docketId: string, state: DocketDemoState): void {
  localStorage.setItem(`${DOCKET_STATE_PREFIX}${docketId}`, JSON.stringify(state));
}

export function patchDocketDemoState(
  docketId: string,
  patch: Partial<DocketDemoState>,
): DocketDemoState {
  const current = loadDocketDemoState(docketId);
  const next = { ...current, ...patch };
  saveDocketDemoState(docketId, next);
  return next;
}

export function savePodImage(docketId: string, dataUrl: string): void {
  localStorage.setItem(`${POD_IMAGE_PREFIX}${docketId}`, dataUrl);
}

export function loadPodImage(docketId: string): string | null {
  return localStorage.getItem(`${POD_IMAGE_PREFIX}${docketId}`);
}

/** Returns persisted demo state only when demo mode is enabled; otherwise empty. */
export function getEffectiveDemoState(docketId: string, enabled: boolean): DocketDemoState {
  if (!enabled) {
    return {};
  }
  return loadDocketDemoState(docketId);
}

/** Clears all per-docket demo state and POD images from localStorage. */
export function clearAllDemoStorage(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (
      key &&
      (key.startsWith(DOCKET_STATE_PREFIX) || key.startsWith(POD_IMAGE_PREFIX))
    ) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}
